import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';

/**
 * Pill-shaped select for toolbar/filter contexts.
 *
 * Renders a <button> trigger (not a native <select>). Default size is exactly
 * 44px (h-11), matching FormSelect and .input-admin fields. Uses rounded-full
 * for the pill shape that distinguishes toolbar controls from full-width form
 * fields.
 *
 * Theme support:
 *   admin  → red focus ring, red selection highlight
 *   employee → blue focus ring, blue selection highlight
 *
 * Options support `isGroupHeader` (non-selectable label) and `disabled`
 * (rendered muted + non-selectable, e.g. "coming soon" items).
 *
 * `size="compact"` is the 28px micro tier for selects nested inside another
 * control (e.g. Auto-refresh interval inside a status chip). Same Lucide
 * ChevronDown + dropdown polish, with a lighter chevron (`text-gray-400`,
 * stroke 1.5) so it doesn't optically overpower sibling 12px icons. Do NOT
 * use compact for standalone toolbar filters (those stay at the 44px default).
 *
 * `compactOnMobile` collapses the trigger to an icon-only pill below the `sm`
 * breakpoint (label hidden, min-width dropped), expanding to the full labelled
 * pill at `sm+`. Useful in tight headers where the label is non-essential on
 * phones. Requires an `icon` to remain meaningful when collapsed.
 *
 * @example
 * <PillSelect
 *   options={[{ value: 'a', label: 'Alpha' }, { value: 'b', label: 'Beta', disabled: true }]}
 *   value={selected}
 *   onChange={setSelected}
 *   placeholder="Select..."
 *   icon={Calendar}
 *   iconColor="text-red-600"
 *   theme="admin"
 *   ariaLabel="Select reporting month"
 * />
 */
const PillSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  icon: Icon,
  iconColor = 'text-gray-500',
  theme = 'admin',
  minWidth = 'min-w-[160px]',
  disabled = false,
  className = '',
  compactOnMobile = false,
  size = 'default',
  ariaLabel,
}) => {
  const isAdmin = theme === 'admin';
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const listRef = useRef(null);
  const dropdownRef = useClickOutside(() => setIsOpen(false), isOpen);

  const selectableOptions = options.filter(o => !o.isGroupHeader);

  const selected = selectableOptions.find(o => String(o.value) === String(value));
  const label = selected ? selected.label : placeholder;

  useEffect(() => {
    if (!isOpen) setHighlightIdx(-1);
  }, [isOpen]);

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIdx];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  const select = useCallback((optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIdx(prev => (prev + 1) % selectableOptions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIdx(prev => (prev - 1 + selectableOptions.length) % selectableOptions.length);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (
          highlightIdx >= 0 &&
          selectableOptions[highlightIdx] &&
          !selectableOptions[highlightIdx].disabled
        ) {
          select(selectableOptions[highlightIdx].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'Home':
        e.preventDefault();
        setHighlightIdx(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightIdx(selectableOptions.length - 1);
        break;
      default:
        break;
    }
  }, [isOpen, highlightIdx, selectableOptions, select]);

  const focusRing = isAdmin
    ? 'focus:ring-red-500 focus:border-red-500'
    : 'focus:ring-blue-500 focus:border-blue-500';

  const selectedHighlight = isAdmin
    ? 'text-red-600 font-medium bg-red-50'
    : 'text-blue-700 font-medium bg-blue-50';

  const isFullWidth = className.includes('w-full');
  const isCompact = size === 'compact';

  // Compact-on-mobile: collapse to an icon-only pill below `sm` (label hidden,
  // no fixed min-width), then expand to the full labelled pill at `sm+`. Keeps
  // a tooltip/aria-label so the current selection stays discoverable.
  // Keep the (literal) minWidth for sm+, but neutralise it below sm so the
  // pill can hug its icon. `sm:${minWidth}` is avoided on purpose — Tailwind's
  // JIT only emits classes that appear literally in source, so a concatenated
  // responsive class would never be generated.
  const widthClass = compactOnMobile ? `${minWidth} max-sm:!min-w-0` : minWidth;
  const triggerPadding = isCompact
    ? 'px-1.5'
    : compactOnMobile
      ? 'px-3 sm:px-4'
      : 'px-4';
  const labelClass = compactOnMobile && !isCompact ? 'hidden sm:block sm:flex-1' : 'flex-1';
  const menuAlign = compactOnMobile ? 'right-0 sm:left-0' : 'left-0';
  const menuMinWidth = isCompact || compactOnMobile ? 'min-w-[180px]' : 'min-w-full';
  const triggerSizeClass = isCompact
    ? 'h-7 gap-1 rounded-md shadow-none text-xs font-medium'
    : 'h-11 gap-1.5 rounded-full shadow-sm text-sm font-medium';
  // Compact chevrons sit beside other 12px Lucide glyphs (e.g. LiveClock) inside
  // status chips — use gray-400 + 1.5 stroke so they don't optically overpower
  // the chip (default stroke-2 at 12px reads like a native caret). Matches
  // CalendarSelect / FormSelect chevron treatment.
  const chevronClass = isCompact
    ? 'w-3 h-3 text-gray-400'
    : 'w-3.5 h-3.5 text-gray-500';
  const chevronStroke = isCompact ? 1.5 : 2;
  const iconSizeClass = isCompact ? 'w-3.5 h-3.5' : 'w-4 h-4';
  // Compact nested selects use a 1px focus ring (not 2px) so the ring doesn't
  // blow past the parent chip's padding.
  const compactFocusRing = isAdmin
    ? 'focus:ring-1 focus:ring-red-500 focus:border-red-500'
    : 'focus:ring-1 focus:ring-blue-500 focus:border-blue-500';
  const activeFocusRing = isCompact ? compactFocusRing : `focus:ring-2 ${focusRing}`;

  return (
    <div className={`relative ${isFullWidth ? 'w-full' : ''}`} ref={dropdownRef}>
      <button
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel || (compactOnMobile || isCompact ? label : undefined)}
        title={compactOnMobile || isCompact ? label : undefined}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`flex items-center bg-white border border-gray-200 ${triggerSizeClass} ${triggerPadding} hover:border-gray-300 transition-colors ${widthClass} focus:outline-none ${activeFocusRing} text-gray-900 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      >
        {Icon && <Icon className={`${iconSizeClass} ${iconColor} flex-shrink-0`} />}
        <span className={`truncate ${labelClass} text-left ${!selected ? 'text-gray-400' : ''}`}>{label}</span>
        <ChevronDown
          className={`${chevronClass} flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          strokeWidth={chevronStroke}
        />
      </button>
      {isOpen && (
        <div
          role="listbox"
          ref={listRef}
          className={`absolute ${menuAlign} z-50 mt-1 ${menuMinWidth} bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto`}
        >
          {(() => {
            let selectableIdx = 0;
            return options.map((option, rawIdx) => {
              if (option.isGroupHeader) {
                return (
                  <div
                    key={`group-${rawIdx}`}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-t border-gray-100 first:border-t-0"
                  >
                    {option.label}
                  </div>
                );
              }
              const idx = selectableIdx++;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={String(value) === String(option.value)}
                  aria-disabled={option.disabled || undefined}
                  disabled={option.disabled}
                  onClick={() => !option.disabled && select(option.value)}
                  onMouseEnter={() => !option.disabled && setHighlightIdx(idx)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    option.disabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : String(value) === String(option.value)
                        ? selectedHighlight
                        : highlightIdx === idx
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              );
            });
          })()}
          {selectableOptions.length === 0 && (
            <div className="px-3 py-4 text-sm text-gray-400 text-center">No options</div>
          )}
        </div>
      )}
    </div>
  );
};

export default PillSelect;
